import { useEffect, useState } from "react";
import { Plus, Ban, Shield, Trash2, Upload, X, FileText, Activity } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import DocumentScannerBox, { applyOcrToCustomerForm } from "../../components/DocumentScannerBox";
import AddressAutocomplete from "../../components/AddressAutocomplete";
import LocationMap from "../../components/LocationMap";

const RISK = {
  basso: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  medio: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  alto: "bg-[#ff0033]/15 text-[#ff5577] border-[#ff0033]/40",
};

const DANNI_PRESET = [
  "Mancato pagamento", "Restituzione auto danneggiata", "Sinistro non denunciato",
  "Targhe scadute", "Multe non pagate", "Carburante mancante", "Interno sporco/rovinato",
  "Furto/Appropriazione", "Documenti falsi", "Guida in stato alterato",
  "Pneumatici danneggiati", "Cerchi rovinati", "Vetri rotti", "Luci danneggiate",
  "Sottoscocca / Marmitta", "Carrozzeria - graffi profondi",
];

const empty = {
  nome: "", cognome: "", email: "", pec: "", codice_destinatario: "",
  email_contratti: "", whatsapp_contratti: "",
  telefono: "", tipo: "privato",
  ragione_sociale: "", piva: "", codice_fiscale: "",
  indirizzo: "", citta: "", cap: "", provincia: "", nazione: "Italia",
  patente_numero: "", patente_emissione: "", patente_scadenza: "", patente_comune: "", patente_categoria: "B",
  ci_numero: "", ci_emissione: "", ci_scadenza: "", ci_comune: "",
  rischio: "basso", note: "",
  conducente_2: null, conducente_3: null, documenti: [],
};

export default function Customers() {
  const [list, setList] = useState([]);
  const [show, setShow] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(empty);
  const [tab, setTab] = useState("anag");
  const [blForm, setBlForm] = useState(null); // {customer, motivo, dettagli:[], note}

  const load = () => api.get("/customers?limit=500").then((r) => setList(Array.isArray(r.data) ? r.data : (r.data?.items || [])));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editId) await api.put(`/customers/${editId}`, form);
      else await api.post("/customers", form);
      toast.success(editId ? "Cliente aggiornato" : "Cliente creato");
      setShow(false); setForm(empty); setEditId(null); setTab("anag");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 4 * 1024 * 1024) return toast.error("Max 4MB");
    const reader = new FileReader();
    reader.onload = () => {
      setForm((f) => ({ ...f, documenti: [...(f.documenti || []), { nome: file.name, tipo: file.type, data: reader.result, ts: new Date().toISOString() }] }));
      toast.success("Documento allegato");
    };
    reader.readAsDataURL(file);
  };

  const openBl = (c) => setBlForm({ customer: c, motivo: c.blacklist_motivo || "", dettagli: c.blacklist_dettagli || [], note: "" });

  const confirmBl = async () => {
    const motivo = `${blForm.motivo}${blForm.dettagli.length ? ' - ' + blForm.dettagli.join(', ') : ''}${blForm.note ? ' | Note: ' + blForm.note : ''}`;
    await api.post(`/customers/${blForm.customer.id}/blacklist?motivo=${encodeURIComponent(motivo)}`);
    // Also persist dettagli on customer
    await api.put(`/customers/${blForm.customer.id}`, { ...blForm.customer, blacklist_motivo: motivo, blacklist_dettagli: blForm.dettagli });
    toast.success(blForm.customer.blacklist ? "Cliente sbloccato" : "Cliente in blacklist");
    setBlForm(null); load();
  };

  const startEdit = (c) => { setForm({ ...empty, ...c }); setEditId(c.id); setShow(true); setTab("anag"); };

  const remove = async (id) => {
    if (!window.confirm("Eliminare?")) return;
    await api.delete(`/customers/${id}`); load();
  };

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-black uppercase tracking-tight">Clienti</h1>
          <p className="text-sm text-white/45 mt-1">{list.length} clienti · {list.filter(c => c.blacklist).length} blacklist · {list.filter(c => c.risk_level === 'critical' || c.risk_level === 'high').length} ad alto rischio</p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="risk-recompute-all"
            onClick={async () => {
              try {
                const r = await api.post("/risk/recompute-all");
                toast.success(`Risk Score ricalcolato: ${r.data.updated} clienti · ${r.data.high_risk_count} ad alto rischio`);
                load();
              } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
            }}
            className="px-4 py-2 rounded-full text-xs bg-amber-500/10 border border-amber-500/40 text-amber-300 hover:bg-amber-500/20 inline-flex items-center gap-2"
          >
            🎯 Ricalcola Risk Score
          </button>
          <button data-testid="cust-add" onClick={() => { setForm(empty); setEditId(null); setShow(true); }} className="btn-neon px-5 py-2 rounded-full text-xs inline-flex items-center gap-2">
            <Plus size={14} /> Nuovo Cliente
          </button>
        </div>
      </div>

      <div className="glass rounded-2xl overflow-x-auto -mx-3 sm:mx-0 px-3 sm:px-0">
        <table className="w-full min-w-[760px] text-sm">
          <thead className="bg-white/[0.03] text-[10px] uppercase tracking-widest text-white/40">
            <tr>
              <th className="text-left p-4">Cliente</th><th className="text-left p-4">Tipo / CF / P.IVA</th>
              <th className="text-left p-4">Contatti</th><th className="p-4">Rischio</th>
              <th className="p-4">Stato</th><th className="p-4"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {list.map((c) => (
              <tr key={c.id} onClick={() => startEdit(c)} className="hover:bg-white/[0.02] cursor-pointer" data-testid={`cust-${c.id}`}>
                <td className="p-4">
                  <div className="font-medium">{c.ragione_sociale || `${c.nome} ${c.cognome}`}</div>
                  <div className="text-xs text-white/40">{c.documenti?.length ? `${c.documenti.length} doc allegati` : 'Nessun doc'}</div>
                </td>
                <td className="p-4">
                  <div className="text-xs uppercase tracking-wider text-white/40">{c.tipo || 'privato'}</div>
                  <div className="text-xs font-mono">{c.codice_fiscale || c.piva || "—"}</div>
                </td>
                <td className="p-4">
                  <div className="text-xs">{c.email}</div>
                  <div className="text-xs text-white/40">{c.telefono}</div>
                </td>
                <td className="p-4 text-center">
                  <div className="flex flex-col items-center gap-1">
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border ${RISK[c.rischio || 'basso']}`}>{c.rischio}</span>
                    {typeof c.risk_score === 'number' && (
                      <span data-testid={`risk-${c.id}`} className={`text-[10px] font-mono px-2 py-0.5 rounded ${
                        c.risk_level === 'critical' ? 'bg-rose-500/20 text-rose-300 border border-rose-500/40' :
                        c.risk_level === 'high' ? 'bg-orange-500/20 text-orange-300 border border-orange-500/40' :
                        c.risk_level === 'medium' ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40' :
                        'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                      }`}>
                        🎯 {c.risk_score}/100
                      </span>
                    )}
                  </div>
                </td>
                <td className="p-4 text-center">
                  {c.blacklist ? (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-[#ff0033]/20 text-[#ff5577] border border-[#ff0033]/40">
                      <Ban size={10} /> Blacklist
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                      <Shield size={10} /> OK
                    </span>
                  )}
                </td>
                <td className="p-4 text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="flex justify-end gap-1">
                    <a href={`/admin/customers/${c.id}/insights`} data-testid={`insights-${c.id}`}
                      className="p-2 rounded-lg bg-white/[0.04] hover:bg-blue-500/15 text-white/60 hover:text-blue-300 inline-flex items-center" title="Vista 360°">
                      <Activity size={13} />
                    </a>
                    <button onClick={() => openBl(c)} data-testid={`bl-${c.id}`}
                      className={`p-2 rounded-lg ${c.blacklist ? "bg-emerald-500/15 text-emerald-300" : "bg-white/[0.04] hover:bg-[#ff0033]/15 text-white/60 hover:text-[#ff0033]"}`}>
                      <Ban size={13} />
                    </button>
                    <button onClick={() => remove(c.id)} className="p-2 rounded-lg bg-white/[0.04] hover:bg-[#ff0033]/15 text-white/60 hover:text-[#ff0033]">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* FORM */}
      {show && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-6" onClick={() => setShow(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="w-full max-w-3xl glass-strong rounded-3xl p-6 max-h-[92vh] overflow-auto" data-testid="cust-form">
            <div className="flex items-center justify-between mb-4">
              <div className="font-display text-2xl font-bold">{editId ? "Modifica Cliente" : "Nuovo Cliente"}</div>
              <div className="flex gap-2">
                {["privato", "societa"].map((t) => (
                  <button type="button" key={t} onClick={() => setForm({ ...form, tipo: t })}
                    className={`px-3 py-1.5 rounded-full text-[10px] uppercase tracking-widest border ${form.tipo === t ? "bg-[#ff0033]/15 border-[#ff0033] text-[#ff5577]" : "bg-white/[0.03] border-white/10 text-white/50"}`}>
                    {t === "societa" ? "Società" : "Privato"}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-1 mb-4 text-[11px] uppercase tracking-widest">
              {[
                ["anag", "Anagrafica"], ["patente", "Patente"], ["ci", "Carta ID"],
                ["cond", "Conducenti"], ["doc", "Documenti"],
              ].map(([k, l]) => (
                <button type="button" key={k} onClick={() => setTab(k)}
                  className={`px-3 py-1.5 rounded-full ${tab === k ? "bg-[#ff0033]/15 text-[#ff5577] border border-[#ff0033]/40" : "text-white/40 hover:text-white"}`}>
                  {l}
                </button>
              ))}
            </div>

            {tab === "anag" && (
              <div className="space-y-3">
                {/* === SCANNER OCR CLIENTE === */}
                {!editId && (
                  <DocumentScannerBox context="customers"
                    testIdPrefix="cust-scan"
                    title="Scansione AI documenti"
                    subtitle="→ Auto-compila Anagrafica + Patente + CI"
                    description="Carica fronte/retro della Carta d'Identità, Patente, Passaporto o qualsiasi documento. L'AI estrae nome, cognome, CF, indirizzo, numero documento, date e ente emissione. Puoi sempre compilare manualmente sotto come fallback."
                    onResult={(p) => applyOcrToCustomerForm(setForm, p)}
                  />
                )}

              <div className="grid grid-cols-2 gap-3">
                {form.tipo === "societa" ? (
                  <>
                    <Input span={2} label="Ragione Sociale" v={form.ragione_sociale} onC={(v) => setForm({ ...form, ragione_sociale: v })} req />
                    <Input label="P.IVA" v={form.piva} onC={(v) => setForm({ ...form, piva: v })} />
                    <Input label="Codice Fiscale" v={form.codice_fiscale} onC={(v) => setForm({ ...form, codice_fiscale: v })} />
                  </>
                ) : (
                  <>
                    <Input label="Nome" v={form.nome} onC={(v) => setForm({ ...form, nome: v })} req />
                    <Input label="Cognome" v={form.cognome} onC={(v) => setForm({ ...form, cognome: v })} req />
                    <Input span={2} label="Codice Fiscale" v={form.codice_fiscale} onC={(v) => setForm({ ...form, codice_fiscale: v.toUpperCase() })} />
                  </>
                )}
                <Input label="Email" v={form.email} onC={(v) => setForm({ ...form, email: v })} req type="email" />
                <Input label="Telefono" v={form.telefono} onC={(v) => setForm({ ...form, telefono: v })} req />
                <Input label="PEC (fatturazione elettronica)" v={form.pec || ""} onC={(v) => setForm({ ...form, pec: v })} type="email" />
                <Input label="Codice Destinatario SDI (7 char)" v={form.codice_destinatario || ""} onC={(v) => setForm({ ...form, codice_destinatario: v.toUpperCase().slice(0, 7) })} />
                <Input label="📧 Email gestione contratti (firma OTP, dispatch)" v={form.email_contratti || ""} onC={(v) => setForm({ ...form, email_contratti: v })} type="email" />
                <Input label="📱 WhatsApp gestione contratti (E.164, es. +393331234567)" v={form.whatsapp_contratti || ""} onC={(v) => setForm({ ...form, whatsapp_contratti: v })} />
                <div className="col-span-2">
                  <label className="text-[10px] uppercase tracking-widest text-white/40">Indirizzo · Autocomplete globale</label>
                  <div className="mt-1">
                    <AddressAutocomplete
                      testid="cust-addr"
                      value={form.indirizzo}
                      onChange={(v) => setForm({ ...form, indirizzo: v })}
                      onSelect={(p) => setForm({
                        ...form,
                        indirizzo: p.indirizzo,
                        citta: p.citta || form.citta,
                        cap: p.cap || form.cap,
                        provincia: p.provincia || form.provincia,
                        nazione: p.nazione || form.nazione,
                        lat: p.lat, lng: p.lng,
                      })}
                      placeholder="Inizia a scrivere la via, città o CAP…"
                    />
                  </div>
                </div>
                <Input label="Città" v={form.citta} onC={(v) => setForm({ ...form, citta: v })} />
                <Input label="CAP" v={form.cap} onC={(v) => setForm({ ...form, cap: v })} />
                <Input label="Provincia" v={form.provincia} onC={(v) => setForm({ ...form, provincia: v.toUpperCase().slice(0, 4) })} />
                <Input label="Nazione" v={form.nazione} onC={(v) => setForm({ ...form, nazione: v })} />
                {(form.lat && form.lng) && (
                  <div className="col-span-2">
                    <LocationMap
                      lat={form.lat} lng={form.lng}
                      label={`${form.indirizzo}${form.citta ? `, ${form.citta}` : ""}`}
                      height={200}
                      testid="cust-map"
                    />
                  </div>
                )}
                <div className="col-span-2">
                  <label className="text-[10px] uppercase tracking-widest text-white/40">Rischio</label>
                  <select value={form.rischio} onChange={(e) => setForm({ ...form, rischio: e.target.value })}
                    className="mt-1 w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-sm">
                    {["basso", "medio", "alto"].map((o) => <option key={o}>{o}</option>)}
                  </select>
                </div>
              </div>
              </div>
            )}

            {tab === "patente" && (
              <div className="space-y-3">
                <DocumentScannerBox context="customers"
                  testIdPrefix="cust-scan-patente"
                  title="Scansione AI · Patente"
                  subtitle="→ Auto-compila i campi sotto"
                  description="Carica fronte/retro della patente di guida (foto o PDF). L'AI estrae numero, categoria, data emissione, data scadenza ed ente di rilascio."
                  variant="compact"
                  onResult={(p) => applyOcrToCustomerForm(setForm, p)}
                />
                <div className="grid grid-cols-2 gap-3">
                  <Input label="Numero Patente" v={form.patente_numero} onC={(v) => setForm({ ...form, patente_numero: v })} />
                  <Input label="Categoria" v={form.patente_categoria} onC={(v) => setForm({ ...form, patente_categoria: v.toUpperCase() })} />
                  <Input label="Data Emissione" type="date" v={form.patente_emissione} onC={(v) => setForm({ ...form, patente_emissione: v })} />
                  <Input label="Data Scadenza" type="date" v={form.patente_scadenza} onC={(v) => setForm({ ...form, patente_scadenza: v })} />
                  <Input span={2} label="Comune di Rilascio (Motorizzazione/UCO)" v={form.patente_comune} onC={(v) => setForm({ ...form, patente_comune: v })} />
                </div>
              </div>
            )}

            {tab === "ci" && (
              <div className="space-y-3">
                <DocumentScannerBox context="customers"
                  testIdPrefix="cust-scan-ci"
                  title="Scansione AI · Carta d'Identità"
                  subtitle="→ Auto-compila i campi sotto"
                  description="Carica fronte/retro della Carta d'Identità (foto o PDF). L'AI estrae numero, comune di rilascio, data emissione e data scadenza."
                  variant="compact"
                  onResult={(p) => applyOcrToCustomerForm(setForm, p)}
                />
                <div className="grid grid-cols-2 gap-3">
                  <Input label="Numero Carta ID" v={form.ci_numero} onC={(v) => setForm({ ...form, ci_numero: v })} />
                  <Input label="Comune di Rilascio" v={form.ci_comune} onC={(v) => setForm({ ...form, ci_comune: v })} />
                  <Input label="Data Emissione" type="date" v={form.ci_emissione} onC={(v) => setForm({ ...form, ci_emissione: v })} />
                  <Input label="Data Scadenza" type="date" v={form.ci_scadenza} onC={(v) => setForm({ ...form, ci_scadenza: v })} />
                </div>
              </div>
            )}

            {tab === "cond" && (
              <div className="space-y-4">
                <DocumentScannerBox context="customers"
                  testIdPrefix="cust-scan-cond"
                  title="Scansione AI · Patente conducenti aggiuntivi"
                  subtitle="→ Compila il 1° conducente abilitato"
                  description="Carica la patente del 2° o 3° conducente. L'AI estrae i dati e li applica al primo slot conducente abilitato sotto. Compila manualmente se preferisci."
                  variant="compact"
                  onResult={(payload) => {
                    const persona = payload?.persona || {};
                    const doc = payload?.documento || {};
                    if (doc.tipo !== "patente") {
                      toast.error("Il documento caricato non è una patente. Carica solo patenti per i conducenti.");
                      return;
                    }
                    const target = form.conducente_2 ? "conducente_2" : "conducente_3";
                    setForm((f) => ({
                      ...f,
                      [target]: {
                        ...(f[target] || {}),
                        nome: persona.nome || (f[target] || {}).nome || "",
                        cognome: persona.cognome || (f[target] || {}).cognome || "",
                        patente: doc.numero || (f[target] || {}).patente || "",
                        scadenza: doc.data_scadenza || (f[target] || {}).scadenza || "",
                      },
                    }));
                  }}
                />
                {[2, 3].map((n) => {
                  const key = `conducente_${n}`;
                  const c = form[key] || {};
                  return (
                    <div key={n} className="p-4 rounded-xl border border-white/10 bg-white/[0.02]">
                      <div className="flex items-center justify-between mb-3">
                        <div className="font-display text-sm font-bold uppercase tracking-wider">{n}° Conducente</div>
                        <label className="flex items-center gap-2 text-xs">
                          <input type="checkbox" checked={!!form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.checked ? {} : null })} />
                          Abilitato
                        </label>
                      </div>
                      {form[key] && (
                        <div className="grid grid-cols-2 gap-2">
                          <Input label="Nome" v={c.nome || ""} onC={(v) => setForm({ ...form, [key]: { ...c, nome: v } })} />
                          <Input label="Cognome" v={c.cognome || ""} onC={(v) => setForm({ ...form, [key]: { ...c, cognome: v } })} />
                          <Input label="Patente" v={c.patente || ""} onC={(v) => setForm({ ...form, [key]: { ...c, patente: v } })} />
                          <Input label="Scadenza" type="date" v={c.scadenza || ""} onC={(v) => setForm({ ...form, [key]: { ...c, scadenza: v } })} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {tab === "doc" && (
              <div className="space-y-4">
                <DocumentScannerBox context="customers"
                  testIdPrefix="cust-scan-doc"
                  title="Scansione AI documenti"
                  subtitle="→ Auto-compila e allega"
                  description="Carica foto patente, CI, passaporto, libretto, voucher, contratti precedenti o PDF di qualsiasi tipo. L'AI estrae i dati e aggiorna l'anagrafica del cliente in autonomia."
                  onResult={(p) => applyOcrToCustomerForm(setForm, p)}
                />
                <div className="text-[10px] uppercase tracking-widest text-white/40 mt-2">Documenti allegati manualmente</div>
                <label className="block cursor-pointer p-6 border-2 border-dashed border-white/15 rounded-xl text-center hover:border-[#ff0033]/40 transition">
                  <Upload size={20} className="mx-auto mb-2 text-white/40" />
                  <div className="text-sm">Allega PDF, JPG o foto documento</div>
                  <div className="text-xs text-white/40 mt-1">max 4MB per file</div>
                  <input type="file" accept="image/*,application/pdf" className="hidden" onChange={onFile} />
                </label>
                <div className="space-y-2">
                  {(form.documenti || []).map((d, i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-white/[0.03] border border-white/5">
                      <div className="flex items-center gap-3 min-w-0">
                        <FileText size={14} className="text-[#ff0033]" />
                        <div className="min-w-0">
                          <div className="text-sm truncate">{d.nome}</div>
                          <div className="text-[10px] text-white/40">{d.tipo}</div>
                        </div>
                      </div>
                      <button type="button" onClick={() => setForm({ ...form, documenti: form.documenti.filter((_, j) => j !== i) })}
                        className="p-1.5 rounded hover:bg-[#ff0033]/15 text-white/60 hover:text-[#ff0033]">
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-3 mt-6 justify-end">
              <button type="button" onClick={() => setShow(false)} className="px-5 py-2.5 rounded-full text-sm bg-white/[0.04] border border-white/10">Annulla</button>
              <button data-testid="cust-form-submit" type="submit" className="btn-neon px-6 py-2.5 rounded-full text-sm">{editId ? "Salva" : "Crea"}</button>
            </div>
          </form>
        </div>
      )}

      {/* BLACKLIST MODAL */}
      {blForm && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-6" onClick={() => setBlForm(null)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg glass-strong rounded-3xl p-6">
            <div className="font-display text-xl font-bold mb-3 flex items-center gap-2">
              <Ban size={18} className="text-[#ff0033]" /> {blForm.customer.blacklist ? "Rimuovi da Blacklist" : "Aggiungi a Blacklist"}
            </div>
            <div className="text-sm text-white/55 mb-4">
              <b>{blForm.customer.ragione_sociale || `${blForm.customer.nome} ${blForm.customer.cognome}`}</b> · CF: {blForm.customer.codice_fiscale || "—"}
            </div>
            {!blForm.customer.blacklist && (
              <>
                <div className="mb-4">
                  <label className="text-[10px] uppercase tracking-widest text-white/40">Causale principale</label>
                  <input data-testid="bl-motivo" value={blForm.motivo} onChange={(e) => setBlForm({ ...blForm, motivo: e.target.value })}
                    className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" placeholder="Es. Mancato pagamento + danno auto" />
                </div>
                <div className="mb-4">
                  <label className="text-[10px] uppercase tracking-widest text-white/40 mb-2 block">Dettagli danni (click per aggiungere)</label>
                  <div className="flex flex-wrap gap-2">
                    {DANNI_PRESET.map((d) => {
                      const active = blForm.dettagli.includes(d);
                      return (
                        <button key={d} type="button" onClick={() => setBlForm({
                          ...blForm,
                          dettagli: active ? blForm.dettagli.filter(x => x !== d) : [...blForm.dettagli, d],
                        })}
                          className={`text-[11px] px-2.5 py-1 rounded-full border ${active ? "bg-[#ff0033]/20 text-[#ff5577] border-[#ff0033]/40" : "bg-white/[0.03] border-white/10 text-white/60 hover:border-white/30"}`}>
                          {d}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="mb-4">
                  <label className="text-[10px] uppercase tracking-widest text-white/40">Note interne</label>
                  <textarea value={blForm.note} onChange={(e) => setBlForm({ ...blForm, note: e.target.value })} rows={2}
                    className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" />
                </div>
              </>
            )}
            <div className="flex gap-3 justify-end">
              <button onClick={() => setBlForm(null)} className="px-5 py-2.5 rounded-full text-sm bg-white/[0.04] border border-white/10">Annulla</button>
              <button data-testid="bl-confirm" onClick={confirmBl} className="btn-neon px-6 py-2.5 rounded-full text-sm">
                {blForm.customer.blacklist ? "Sblocca" : "Conferma Blacklist"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Input = ({ label, v, onC, type = "text", req, span = 1 }) => (
  <div className={span === 2 ? "col-span-2" : ""}>
    <label className="text-[10px] uppercase tracking-widest text-white/40">{label}</label>
    <input type={type} required={req} value={v || ""} onChange={(e) => onC(e.target.value)}
      className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[#ff0033]/60 focus:outline-none" />
  </div>
);
